import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { Field } from '../components/Field.js';
import { AuthLayout } from './AuthLayout.js';

type State = 'idle' | 'verifying' | 'verified' | 'failed';

/**
 * Email confirmation.
 *
 * The emailed link lands here with `?token=…`. When no token is present the
 * page becomes the "resend" form instead, which is also where an expired link
 * sends the user.
 */
export function VerifyEmailPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState] = useState<State>(token === null ? 'idle' : 'verifying');
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  // React 18 StrictMode mounts effects twice in development; the token is
  // single-use, so a second redemption would report a spurious failure.
  const attempted = useRef(false);

  useEffect(() => {
    if (token === null || attempted.current) return;
    attempted.current = true;

    void (async () => {
      try {
        await apiRequest('/auth/verify-email', { method: 'POST', body: { token } });
        setState('verified');
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught.message : 'This confirmation link could not be used.',
        );
        setState('failed');
      }
    })();
  }, [token]);

  const handleResend = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setResending(true);
    try {
      await apiRequest('/auth/resend-verification', { method: 'POST', body: { email } });
    } finally {
      // Always reports the same outcome: whether the address has an account is
      // not disclosed (ADR 0010).
      setResent(true);
      setResending(false);
    }
  };

  if (state === 'verifying') {
    return (
      <AuthLayout title="Confirming your email">
        <p role="status" aria-live="polite">
          Checking your confirmation link…
        </p>
      </AuthLayout>
    );
  }

  if (state === 'verified') {
    return (
      <AuthLayout title="Email confirmed">
        <Alert tone="success">Your email address is confirmed.</Alert>
        <Link className="btn btn--primary btn--block" to="/login">
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={state === 'failed' ? 'Link expired or already used' : 'Resend confirmation'}
      subtitle="Enter your email address and we will send a new confirmation link."
      footer={
        <>
          Back to <Link to="/login">sign in</Link>
        </>
      }
    >
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {resent ? (
        <Alert tone="success">
          If that address has an account awaiting confirmation, a new link is on its way.
        </Alert>
      ) : null}

      <form
        onSubmit={(event) => {
          void handleResend(event);
        }}
        noValidate
      >
        <Field
          label="Email address"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <button type="submit" className="btn btn--primary btn--block" disabled={resending}>
          {resending ? 'Sending…' : 'Send a new link'}
        </button>
      </form>
    </AuthLayout>
  );
}
